import React, { useEffect } from 'react';
import {
    Lock,
    Users,
    Wallet,
    Target,
    FileText,
    Mic,
    Eye,
    LayoutDashboard,
    Flame,
    Bot,
    TrendingUp,
    GraduationCap,
    Camera
} from 'lucide-react';

const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        },
        amber: {
            bg: "bg-amber-900/10",
            border: "border-amber-500/30",
            iconBg: "bg-amber-500/10",
            iconColor: "text-amber-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

const NumRow = ({ n, h, s, nw = "w-10 sm:w-12" }) => (
    <div className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
        <div className={`text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 ${nw} whitespace-nowrap tabular-nums`}>{n}</div>
        <div>
            <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{h}</h3>
            <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{s}</p>
        </div>
    </div>
);

export default function SanchitStrategy() {

    useEffect(() => {
        document.title = "Sanchit Kedia | Brand Research";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Trust Fund Baby</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Research</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Built from your answers · 14 Sep 2026
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        The research<br />behind your brand.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-6 font-light">
                        Your positioning, your story spine, your content series and the system that runs without your calendar.<br className="hidden sm:block" /> We studied the five accounts you sent and the market around them. Nothing here is templated.
                    </p>
                </section>

                {/* THE STORY */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The story you already have</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            You gave us the<br />whole brand in one answer.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Your parents held shares and mutual funds in your name. You never saw that money, because an emergency came and it was spent. Almost every Indian adult has a version of that sentence. It is also the exact reason your product exists.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="The gift" s="Your nephew was born and you wanted to give him something that would still matter in twenty years. That is where the trust fund idea started, and it is the warmest possible way into a cold subject." />
                        <NumRow n="02" h="The disappearance" s="Every parent invests for their child. Very few children ever receive it. Not from bad intent, from an emergency and a withdrawal that never got replaced. This chapter is the hook, and it belongs to you." />
                        <NumRow n="03" h="The lock, then the lesson" s="So the money gets locked, even from the parents. Then the harder question you asked yourself: will the child know what to do with it? That is why the education module exists, and that answer is what separates you from a savings scheme." />
                    </div>
                </section>

                {/* POSITIONING */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The positioning</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Built by the kid<br />who never got the money.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You are not a finance guru and you are not selling a scheme. You were on the losing end of the exact problem you are now fixing, and you are building the fix alone. That is a position nobody in this market can borrow.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Lock} heading="You lived the problem" subheading="the money in your name that you never received · nobody has to take your word for why the lock matters, they have their own version of the story" accent="indigo" />
                        <InfoCard icon={Bot} heading="One founder, a fleet of agents" subheading="a one-man fintech built with AI doing the work of a team · you told us to lead with this, and it is the difference parents can actually see" accent="indigo" />
                        <InfoCard icon={Wallet} heading="No incentive to unlock it" subheading="institutions earn when money moves · you have no investors and no churn to feed, which is the quiet reason the lock is real" accent="indigo" />
                    </div>
                </section>

                {/* AUDIENCE READ */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The audience read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What the five accounts<br />told us.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You sent a child psychologist, a yoga teacher, a nomad couple, an early-learning brand and a mom creator. We studied all five. The pattern in that list decides the whole strategy.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto mb-10">
                        <InfoCard icon={Users} heading="Not one finance account" subheading="every account you named is parenting, child development or wellness · your buyers do not open the finance feed, so we do not put you in it" accent="rose" />
                        <InfoCard icon={Target} heading="The seat is empty" subheading="India's big money creators talk to adults about adult money · nobody owns money for your child inside the parenting feed · that seat is yours to take" accent="rose" />
                        <InfoCard icon={GraduationCap} heading="The model is proven" subheading="an early-learning brand in your exact audience built a Shark Tank business on free five-minute activities · same parents, same structure, different subject" accent="rose" />
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="We enter through parenting, not finance" s="Your content sits next to sleep routines and screen-time advice, not next to stock tips. Same parent, completely different state of mind. This is the single biggest decision in this document." />
                        <NumRow n="02" h="Emotion first, returns second" s="Nobody shares a compounding chart. They share the fear that their child will start from zero. We lead with the feeling and let the maths close." />
                        <NumRow n="03" h="Your education module is already the engine" s="Blog, video, interactive game, offline activity. That is the same asset class the early-learning brands grew on, and you already built it." />
                    </div>
                </section>

                {/* CONTENT SERIES */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The content series</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Two series.<br />One story, one number.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Flame} heading="Where Did It Go?" subheading="the money your parents put in your name, and where it actually went · your story plus everyone else's version of it · this is the series that travels" accent="amber" />
                        <InfoCard icon={TrendingUp} heading="The 18-Year Number" subheading="what a fixed monthly amount becomes by the time your child turns 18, and what waiting five years costs · the maths made impossible to ignore" accent="amber" />
                    </div>

                    <div className="max-w-3xl mx-auto mt-10">
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-7 text-center">
                            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed">
                                Every figure in the maths series gets checked against your own calculator and your compliance view before it goes anywhere near a post. Your nephew stays in the story and never on camera.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FUNNEL */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">The funnel</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            From a comment<br />to a funded account.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You were already planning this flow yourself. Here it is, built for you, using an asset you have already made.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="A calculator they can actually use" s="Every video points to one free resource, a calculator or sheet where a parent puts in their own numbers and sees what their child ends up with. Your blog and education module layer in behind it as they go live." />
                        <NumRow n="02" h="Comment to unlock" s="Videos end with one word to comment. A parent who asks for the numbers on their own child has qualified themselves better than any form could." />
                        <NumRow n="03" h="The DM automation delivers and nurtures" s="Our Meta-verified system sends the calculator, answers in your voice, and flags the serious ones. Nothing for you to build, and no ManyChat subscription." />
                        <NumRow n="04" h="The next problem is the product" s="A parent who has just seen what their child could end up with has an obvious next question: where does that money actually sit, and what stops me spending it. That question is Trust Fund Baby, and the full education module stays the reward for investing." />
                    </div>
                </section>

                {/* THE SYSTEM */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">The system</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            One studio day.<br />Then you are out of it.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You said you were never comfortable putting your face out there. You do not have to get comfortable. You have to do it once, properly, and then the system carries it.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Camera} heading="The one session" subheading="book the studio once for 15 to 25 minutes of video · the 2 hours of audio you can record at home in your own time · we send the exact shot list before you go" accent="emerald" />
                        <InfoCard icon={Bot} heading="Your clone takes over" subheading="built on our own Indian skin-tone model, not a stock avatar · after that session you never schedule a shoot again" accent="emerald" />
                        <InfoCard icon={Mic} heading="You drop voice notes" subheading="a thought about your nephew, a parent's question, a build update · we ask the right questions to pull the rest out of you" accent="emerald" />
                        <InfoCard icon={FileText} heading="We research and script" subheading="aimed at what parents actually watch, not at what sounds correct · hooks, structure and words in your voice" accent="emerald" />
                        <InfoCard icon={Eye} heading="You approve, we ship" subheading="nothing posts without your sign off · daily cadence, one visual identity across the page" accent="emerald" />
                        <InfoCard icon={LayoutDashboard} heading="The data runs the loop" subheading="every video tracked in your dashboard · what pulls parents gets volume, what pulls the wrong crowd gets cut" accent="emerald" />
                    </div>
                </section>

                {/* THE 5000 */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">Your number</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            5,000 parents<br />by month twelve.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            That is the win you named. Here is the honest breakdown of it, including the part that is not ours.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="The audience. Ours to build." s="A real audience of Indian parents, built from zero, tracked against the 10,000 number every month so you can see exactly where it stands." />
                        <NumRow n="02" h="The conversations. Ours." s="Comments become DMs, DMs get the free calculator, and the serious ones land in your dashboard flagged and warm." />
                        <NumRow n="03" h="The conversion. Yours." s="The landing page, the KYC, the Aadhaar step, the lock. We design the handoff and hand you qualified parents. We do not promise the signature, and any agency that does is guessing." />
                        <NumRow n="04" h="Organic is the half that compounds" s="Ads give you instant volume and stop the day you stop paying. This page gets cheaper every month it runs. Run both, which is exactly what you were already planning." />
                    </div>
                </section>

                {/* 90 DAYS + GUARANTEE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The 90 days</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            The roadmap<br />and the target.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4 mb-10">
                        <NumRow nw="w-24 sm:w-28" n="W1" h="Onboarding" s="Studio session, clone trained, positioning locked, the calculator built, first scripts approved by you. The heaviest week, on purpose." />
                        <NumRow nw="w-24 sm:w-28" n="W2" h="The engine goes live" s="Daily posting starts. Series and hooks tested against real data instead of opinion." />
                        <NumRow nw="w-24 sm:w-28" n="W3-8" h="Test, then double down" s="Whatever pulls parents gets volume. The DM engine goes live once the audience is real, which is also roughly when your BSE registration clears." />
                        <NumRow nw="w-24 sm:w-28" n="W9-12" h="Compound" s="Winning formats scale and the audience is waiting on the day the app goes live. Month one is the slowest month. Month three is the one you screenshot." />
                    </div>

                    <div className="max-w-3xl mx-auto">
                        <div className="rounded-2xl border border-amber-500/20 bg-amber-900/10 p-6 sm:p-8 text-center">
                            <div className="text-amber-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-4">The target</div>
                            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed">
                                10,000 followers in 90 days is the number we build towards, and they are parents rather than passers by. Month one is the slowest, month two is where you see it move, and month three is where it compounds. Starting from zero is not the risk here. It is the case study.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        We lock the number on our call.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        The research is done and the system is ready. Week 1 starts the day we sign, and your audience is built before the app is live.
                    </p>
                    <a
                        href="mailto:suresh@socialseo.in"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        suresh@socialseo.in
                    </a>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
